import {
    DepositFrequency,
    DepositFrequencyAmount,
    minimumIninitialDeposit,
    MinimumInitialDeposit
} from '~/models/deposits/DepositFrequency';
import { PortfolioTypes } from '~/models/PortfolioType';

/** Format deposit amount in cent dollars. */
export const depositAmountInCents = (amount: number): number => {
    return amount * 100;
};

/** Returns credit card fee based on card provider location. */
export default class FeeCalculatorInCents {
    public static getCreditCardFeeUSDByCountry(amount: number, country: string): number {
        // empty string defaults to US origin
        if (country === 'US' || country === '') return FeeCalculatorInCents.getCreditCardFeeUSD(amount);
        return FeeCalculatorInCents.getCreditCardFeeInternational(amount);
    }

    private static getCreditCardFeeUSD(amount: number): number {
        return (amount + 30) / 0.971;
    }

    private static getCreditCardFeeInternational(amount: number): number {
        return (amount + 30) / 0.956;
    }

    public static getBitpayFeesUSD(amount: number): number {
        return amount / 0.99;
    }
}

/** Deposit amount, including any charged fees, formatted in cent dollars. */
export const getDepositAmtWithFeesInCents = (amount: number, paymentType: string, country: string): number => {
    let unroundedTotalInCents = amount;

    if (paymentType === 'card') {
        unroundedTotalInCents = FeeCalculatorInCents.getCreditCardFeeUSDByCountry(unroundedTotalInCents, country);
    }

    if (paymentType === 'bitpay') {
        unroundedTotalInCents = FeeCalculatorInCents.getBitpayFeesUSD(unroundedTotalInCents);
    }

    const roundedTotalInCents = Math.round(unroundedTotalInCents);
    return roundedTotalInCents;
};

/** Min deposit amount calculator. */
export const getMinimumDepositAmount = (
    destination: PortfolioTypes,
    frequency: DepositFrequency,
    cashBalance: number
): number => {
    switch (frequency) {
        case DepositFrequency.once:
            switch (destination) {
                case PortfolioTypes.trading:
                    return minimumIninitialDeposit[PortfolioTypes.trading];
                case PortfolioTypes.managed:
                    if (cashBalance / 100 >= 1) return 1;
                    return MinimumInitialDeposit.Managed - cashBalance / 100;
                default:
                    return MinimumInitialDeposit.Managed;
            }

        case DepositFrequency.week:
            switch (destination) {
                case PortfolioTypes.trading:
                    return minimumIninitialDeposit[PortfolioTypes.trading];
                case PortfolioTypes.managed:
                    return DepositFrequencyAmount.week;
                default:
                    return DepositFrequencyAmount.week;
            }
        case DepositFrequency.month:
            switch (destination) {
                case PortfolioTypes.trading:
                    return minimumIninitialDeposit[PortfolioTypes.trading];
                case PortfolioTypes.managed:
                    return DepositFrequencyAmount.month;
                default:
                    return DepositFrequencyAmount.month;
            }
        default:
            switch (destination) {
                case PortfolioTypes.trading:
                    return minimumIninitialDeposit[PortfolioTypes.trading];
                case PortfolioTypes.managed:
                    return minimumIninitialDeposit[PortfolioTypes.managed];
                default:
                    return Math.max(...Object.values(minimumIninitialDeposit));
            }
    }
};
